// ──────────────────────────────────────────────────────────────
// 월지급 혜택 상세내역 + 담당부처 매핑 — 단일 진실 공급원(single source of truth)
//
// 사용자가 "이번 달 받을 항목"을 자녀별로 펼쳐보는 곳. monthlyAmount(총액)와 짝을 이루며,
// 분기 로직이 많아 금액·항목 오류가 나기 쉬워 모듈로 분리 + 회귀 테스트(scripts/test-benefitBreakdown.js)로 박제.
//   · deptFor(b): 담당 부처 결정 — BEN_DEPT 우선, 없으면 reg/카테고리 폴백
//   · monthlyBreakdown(bs, u): 월지급 혜택을 항목 배열로 분해
//       - 부모급여(id:5): 만0세 100만 / 만1세 50만 (자녀별)
//       - 아동수당(id:7): 만9세 미만 자녀별 10만 (비수도권은 라벨만, 과다표시 방지)
//       - 한부모 아동양육비(id:302): 만18세 미만 자녀별 23만
//       - 그 외 mC 월지급: 매칭 자녀별 b.n
//       - 가족 단위: b.n 1건
// age(나이)는 dateUtils 공유. 테스트 위해 기준시각(now) 선택 인자 지원.
// (2026-06-24 모듈화 마무리 — App.js 인라인에서 분리)
// ──────────────────────────────────────────────────────────────
const { age } = require("./dateUtils");

const BEN_DEPT = {
  // 보건복지부
  1: '복지부', 2: '복지부·지자체', 4: '복지부', 5: '복지부', 7: '복지부', 8: '복지부', 10: '여성가족부',
  11: '복지부·교육부', 17: '복지부', 18: '복지부', 20: '복지부·산업부', 48: '여성가족부', 49: '건강보험공단', 51: '건강보험공단',
  // 건강보험공단·질병관리청
  3: '건강보험공단', 40: '질병관리청', 41: '질병관리청', 42: '질병관리청', 43: '건강보험공단', 50: '질병관리청',
  // 교육부
  9: '교육부', 12: '지자체', 44: '교육부',
  // 국토교통부
  14: '국토부', 15: '국토부·LH',
  // 고용노동부
  45: '고용노동부', 46: '고용노동부', 303: '고용노동부', 402: '고용노동부',
  // 행정안전부·여성가족부
  47: '다부처(행안·산업·기재)',
  // 한부모·통합돌봄
  301: '복지부', 302: '여성가족부',
  // 경찰청·도로공단
  60: '경찰청', 61: '경찰청', 62: '교육부·경찰청(117)', 63: '도로교통공단', 64: '경찰청',
  // 청년·신생아
  400: '국토부', 401: '국토부·주택도시기금', 403: '서울특별시', 404: '복지부·행안부',
  // 지자체 — id 패턴별 자동 매핑은 deptFor()에서 reg 필드 활용
};

function deptFor(b) {
  if (!b) return '';
  if (BEN_DEPT[b.id]) return BEN_DEPT[b.id];
  // 지역 혜택 — reg 필드 활용
  if (b.sc === 'd' && b.reg) return b.reg;
  // 카테고리 기본값
  if (b.c === 'local' && b.reg) return b.reg;
  if (b.c === 'senior') return '복지부';
  if (b.c === 'newlywed') return '국토부·고용부';
  return '정부';
}

// 월지급 혜택 상세내역 (사용자 → 자녀별 분기 포함) — 2026-06-06
function monthlyBreakdown(bs, u, now) {
  const items = [];
  // 상호배타 — 보육료(8)·유아학비(9)·가정양육수당(52)은 하나만 상세 반영, 큰 순 8>9>52 (benefitAmount와 동일 규칙, 총액=상세합 유지)
  const _ids = new Set((bs || []).map(b => b && b.id));
  const _skipCare = new Set(_ids.has(8) ? [9, 52] : _ids.has(9) ? [52] : []);
  (bs || []).forEach(b => {
    if (!b || b.tp !== '월지급') return;
    if (_skipCare.has(b.id)) return;
    if (b.id === 5 && u && u.children && u.children.length) {
      // 부모급여 — 만0세 100만/만1세 50만 자녀별
      u.children.forEach(c => { const a = age(c.birthDate, now); if (a === 0) items.push({ id: b.id + '_' + c.id, name: '부모급여 (' + (c.name || '자녀') + ' 만0세)', dept: '복지부', amount: 1e6, b }); else if (a === 1) items.push({ id: b.id + '_' + c.id, name: '부모급여 (' + (c.name || '자녀') + ' 만1세)', dept: '복지부', amount: 5e5, b }); });
      return;
    }
    if (b.id === 7 && u && u.children && u.children.length) {
      // 아동수당 — 만9세 미만(2026 확대), 월 10만원 전국 공통. 비수도권·인구감소지역은 지자체 추가지원(+5천~최대13만, 지역사랑상품권 등 조건부)이라 과다표시 방지 위해 기본액만 표시하고 안내만.
      const isNonMetro = u.city && !/서울특별시|경기도|인천광역시/.test(u.city);
      u.children.forEach(c => { const a = age(c.birthDate, now); if (a >= 0 && a < 9) items.push({ id: b.id + '_' + c.id, name: '아동수당 (' + (c.name || '자녀') + ' 만' + a + '세)' + (isNonMetro ? ' · 비수도권 추가지원 가능' : ''), dept: '복지부', amount: 1e5, b }); });
      return;
    }
    // 한부모가족 아동양육비 (id:302) — 한부모 자격(mF) 통과 후 18세 미만 자녀별
    if (b.id === 302 && u && u.children && u.children.length) {
      u.children.forEach(c => { const a = age(c.birthDate, now); if (a >= 0 && a < 18) items.push({ id: b.id + '_' + c.id, name: '한부모가족 아동양육비 (' + (c.name || '자녀') + ' 만' + a + '세)' + (a <= 5 ? ' · 추가지원 가능' : ''), dept: '여성가족부', amount: 23e4, b }); });
      return;
    }
    // 그 외 자녀 매칭(mC) 월지급 — 자녀별로 표시 (어린이집 보육료·아이돌봄 등)
    if (b.mC && u && u.children && u.children.length && b.n > 0) {
      u.children.forEach(c => { try { if (b.mC(c)) { const a = age(c.birthDate, now); items.push({ id: b.id + '_' + c.id, name: b.t + ' (' + (c.name || '자녀') + ' 만' + a + '세)', dept: deptFor(b), amount: b.n, b }); } } catch (e) { } });
      return;
    }
    // 가족 단위 — 1건
    items.push({ id: String(b.id), name: b.t, dept: deptFor(b), amount: b.n || 0, b });
  });
  return items;
}

module.exports = { BEN_DEPT, deptFor, monthlyBreakdown };
